<template>
  <div class="obj-list-panel" :class="{ collapsed }">
    <!-- 标题栏 -->
    <div class="panel-header" @click="collapsed = !collapsed">
      <List class="header-icon" :size="18" />
      <span class="header-title">对象列表</span>
      <span class="header-count" v-if="!collapsed">({{ allObjects.length }})</span>
      <span class="collapse-btn">{{ collapsed ? '▼' : '▲' }}</span>
    </div>

    <!-- 内容区 -->
    <div class="panel-body" v-if="!collapsed">
      <!-- 搜索框 -->
      <div class="search-row">
        <input
          v-model="searchText"
          class="search-input"
          placeholder="搜索对象..."
          @click.stop
        />
      </div>

      <!-- 对象树（Shift 范围 / Ctrl 多选；Delete 删除；右键菜单） -->
      <div
        ref="treeScrollRef"
        class="tree-scroll"
        tabindex="0"
        @keydown="onListKeydown"
      >
        <template v-for="group in filteredGroups" :key="group.label">
          <div class="group-header" @click="toggleGroup(group.label)">
            <span class="group-arrow">{{ expandedGroups.has(group.label) ? '▼' : '▶' }}</span>
            <component :is="group.iconComponent" class="group-icon-svg" :size="16" />
            <span class="group-name">{{ group.label }}</span>
            <span class="group-count">{{ group.items.length }}</span>
          </div>

          <template v-if="expandedGroups.has(group.label)">
            <div
              v-for="item in group.items"
              :key="item.id"
              class="obj-row"
              :class="{ selected: isRowSelected(item.id), locked: item.locked, hidden: item.hidden }"
              @click.stop="onRowClick($event, item)"
              @contextmenu.prevent="openRowContextMenu($event, item)"
            >
              <span class="obj-icon">
                <Icon v-if="isIconifyName(item.icon)" :icon="item.icon" class="obj-icon-svg" />
                <span v-else>{{ item.icon }}</span>
              </span>
              <input
                v-if="editingId === item.id"
                ref="renameInputRef"
                v-model="renameDraft"
                class="obj-name-input"
                @click.stop
                @dblclick.stop
                @keydown.enter.prevent="commitRename"
                @keydown.escape.prevent="cancelRename"
                @blur="commitRename"
              />
              <span
                v-else
                class="obj-name"
                title="双击重命名"
                @dblclick.stop="beginRename(item)"
              >{{ item.name }}</span>
              <Lock v-if="item.locked" class="obj-locked" :size="14" title="已锁定" />
              <button
                v-if="item.hidden"
                class="obj-vis-btn"
                @click.stop="toggleHidden(item.id)"
                title="点击显示"
                aria-label="显示对象"
              >
                <EyeOff :size="14" />
              </button>
              <button
                v-else
                class="obj-vis-btn"
                @click.stop="toggleHidden(item.id)"
                title="点击隐藏"
                aria-label="隐藏对象"
              >
                <Eye :size="14" />
              </button>
            </div>
          </template>
        </template>

        <div v-if="filteredGroups.length === 0" class="empty-tip">
          暂无对象
        </div>
      </div>
    </div>

    <!-- 对象列表右键菜单 -->
    <Teleport to="body">
      <div
        v-if="ctxMenu.visible"
        class="obj-list-ctx"
        :style="{ left: ctxMenu.x + 'px', top: ctxMenu.y + 'px' }"
        @click.stop
      >
        <div class="obj-list-ctx-item" @click="toggleLockFromMenu">
          <component :is="ctxMenuTargetLocked ? Unlock : Lock" :size="14" class="obj-list-ctx-icon" />
          <span class="obj-list-ctx-label">{{ ctxMenuTargetLocked ? '解锁编辑' : '锁定编辑' }}</span>
        </div>
        <div class="obj-list-ctx-item" :class="{ disabled: !ctxMenuCanGroup }" @click="groupFromMenu">
          <Package :size="14" class="obj-list-ctx-icon" />
          <span class="obj-list-ctx-label">成组</span>
        </div>
        <div class="obj-list-ctx-item" :class="{ disabled: !ctxMenuTargetHasGroup }" @click="ungroupFromMenu">
          <PackageOpen :size="14" class="obj-list-ctx-icon" />
          <span class="obj-list-ctx-label">取消组合</span>
        </div>
        <div class="obj-list-ctx-sep" />
        <div class="obj-list-ctx-item danger" @click="confirmDeleteFromMenu">
          <Trash2 :size="14" class="obj-list-ctx-icon" />
          <span class="obj-list-ctx-label">删除</span>
          <kbd class="obj-list-ctx-kbd" title="Delete / Backspace">Del</kbd>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, nextTick, watch, onMounted, onUnmounted } from 'vue'
import { exclusiveConfirm } from '@/utils/exclusiveConfirm'
import { List, Lock, Unlock, Building2, Wrench, Cog, Map as MapIcon, Plug, Users, Zap, Tag, Package, PackageOpen, EyeOff, Eye, Wheat, FlaskConical, Droplets, UtensilsCrossed, Trash2 } from 'lucide-vue-next'
import { Icon } from '@iconify/vue'
import { storeToRefs } from 'pinia'
import { useSceneStore, useSelectionStore } from '../stores/scene'
import { registry } from '../components-lib/registry'
import { baseDisplayName, buildObjectDisplayNameMap } from '../utils/objectDisplayNames'
import { categoryLabelZh } from '../utils/componentCategoryLabels'
import { groupObjectIds, setLockedForObjectIds, ungroupObjectIds } from '@/utils/batchObjectOps'

function isIconifyName(icon: string): boolean {
  return icon.includes(':')
}

const props = defineProps<{
  onHistoryPush?: () => void
}>()

const emit = defineEmits<{ (e: 'toggle-hidden', id: string): void }>()
const sceneStore = useSceneStore()
const selectionStore = useSelectionStore()
const { selectedId, selectedIds, renameRequestId } = storeToRefs(selectionStore)

const collapsed = ref(true)
const searchText = ref('')
const expandedGroups = ref(
  new Set<string>(['建筑', '设备', '机加工设备', '设施', '管道', '人员物流', '能源', '标注', '食用菌设备', '陶瓷', '食品矿泉水'])
)

const allObjects = computed(() => sceneStore.activeObjects())
const displayNameMap = computed(() => buildObjectDisplayNameMap(allObjects.value))

const editingId = ref<string | null>(null)
const renameDraft = ref('')
const renameInputRef = ref<HTMLInputElement | null>(null)
const treeScrollRef = ref<HTMLElement | null>(null)
/** Shift 范围选择的锚点（与资源管理器一致：上一次非 Shift 点击的对象） */
const rangeAnchorId = ref<string | null>(null)

const ctxMenu = ref({ visible: false, x: 0, y: 0, objectId: '' })
const ctxMenuTargetLocked = computed(() => {
  const oid = ctxMenu.value.objectId
  return !!sceneStore.activeObjects().find((o) => o.id === oid)?.locked
})
const ctxMenuTargetHasGroup = computed(() => {
  const oid = ctxMenu.value.objectId
  return !!sceneStore.activeObjects().find((o) => o.id === oid)?.groupId
})
const ctxMenuCanGroup = computed(() => selectedIds.value.length >= 2)

interface GroupItem {
  id: string
  name: string
  icon: string
  locked?: boolean
  hidden?: boolean
}
type IconComponent = typeof Building2

interface Group {
  label: string
  iconComponent: IconComponent
  items: GroupItem[]
}

const CATEGORY_META: Record<string, { label: string; iconComponent: IconComponent }> = {
  building:       { label: categoryLabelZh('building'),       iconComponent: Building2 },
  equipment:      { label: categoryLabelZh('equipment'),      iconComponent: Wrench },
  machining:      { label: categoryLabelZh('machining'),      iconComponent: Cog },
  infrastructure: { label: categoryLabelZh('infrastructure'), iconComponent: MapIcon },
  pipe:           { label: categoryLabelZh('pipe'),           iconComponent: Plug },
  personnel:      { label: categoryLabelZh('personnel'),      iconComponent: Users },
  energy:         { label: categoryLabelZh('energy'),         iconComponent: Zap },
  label:          { label: categoryLabelZh('label'),          iconComponent: Tag },
  mushroom:       { label: categoryLabelZh('mushroom'),       iconComponent: Wheat },
  ceramics:       { label: categoryLabelZh('ceramics'),       iconComponent: FlaskConical },
  food_water:     { label: categoryLabelZh('food_water'),     iconComponent: Droplets },
  food_factory:   { label: categoryLabelZh('food_factory'),   iconComponent: UtensilsCrossed },
}

const filteredGroups = computed<Group[]>(() => {
  const search = searchText.value.toLowerCase()
  const grouped = new Map<string, GroupItem[]>()
  const nameMap = displayNameMap.value

  for (const obj of allObjects.value) {
    const def = registry.get(obj.type)
    const category = def?.category ?? 'building'
    const meta = CATEGORY_META[category] ?? { label: categoryLabelZh(category), iconComponent: Package }
    const icon = def?.icon ?? '📦'
    const displayName = nameMap.get(obj.id) ?? baseDisplayName(obj)

    if (search && !displayName.toLowerCase().includes(search) && !obj.type.includes(search)) continue

    if (!grouped.has(meta.label)) grouped.set(meta.label, [])
    grouped.get(meta.label)!.push({
      id: obj.id,
      name: displayName,
      icon,
      locked: obj.locked,
      hidden: obj.hidden
    })
  }

  return (Array.from(grouped.entries()) as [string, GroupItem[]][]).map(([label, items]) => {
    const iconComponent = Object.values(CATEGORY_META).find(v => v.label === label)?.iconComponent ?? Package
    return { label, iconComponent, items }
  })
})

const flatVisibleIds = computed(() => {
  const ids: string[] = []
  for (const g of filteredGroups.value) {
    for (const it of g.items) ids.push(it.id)
  }
  return ids
})

function isRowSelected(id: string): boolean {
  return selectedIds.value.includes(id)
}

function rangeIds(anchor: string, end: string, flat: string[]): string[] {
  const ia = flat.indexOf(anchor)
  const ib = flat.indexOf(end)
  if (ia === -1 || ib === -1) return [end]
  const lo = Math.min(ia, ib)
  const hi = Math.max(ia, ib)
  return flat.slice(lo, hi + 1)
}

function closeCtxMenu() {
  ctxMenu.value.visible = false
}

function openRowContextMenu(e: MouseEvent, item: GroupItem) {
  if (editingId.value === item.id) return
  e.stopPropagation()
  treeScrollRef.value?.focus()
  if (!isRowSelected(item.id)) {
    rangeAnchorId.value = item.id
    selectionStore.requestFocus(item.id)
  }
  ctxMenu.value = { visible: true, x: e.clientX, y: e.clientY, objectId: item.id }
}

function confirmDeleteFromMenu() {
  closeCtxMenu()
  deleteSelectedObjects()
}

function toggleLockFromMenu() {
  const oid = ctxMenu.value.objectId
  if (!oid) return
  props.onHistoryPush?.()
  setLockedForObjectIds(sceneStore, [oid], !ctxMenuTargetLocked.value)
  closeCtxMenu()
}

function groupFromMenu() {
  if (!ctxMenuCanGroup.value) return
  props.onHistoryPush?.()
  groupObjectIds(sceneStore, selectedIds.value)
  closeCtxMenu()
}

function ungroupFromMenu() {
  const oid = ctxMenu.value.objectId
  if (!oid || !ctxMenuTargetHasGroup.value) return
  props.onHistoryPush?.()
  ungroupObjectIds(sceneStore, [oid])
  closeCtxMenu()
}

async function deleteSelectedObjects() {
  const ids =
    selectedIds.value.length > 1
      ? [...selectedIds.value]
      : selectedId.value
        ? [selectedId.value]
        : []
  if (!ids.length) return
  if (ids.length > 1) {
    const ok = await exclusiveConfirm(`确定删除选中的 ${ids.length} 个对象？`, '删除确认')
    if (!ok) return
  }
  props.onHistoryPush?.()
  const toRemove = [...ids]
  selectionStore.selectPath(null)
  selectionStore.deselect()
  toRemove.forEach((id) => sceneStore.removeObject(id))
}

function onListKeydown(e: KeyboardEvent) {
  if (e.key !== 'Delete' && e.key !== 'Backspace') return
  if (editingId.value) return
  const t = e.target as HTMLElement
  if (t?.closest?.('.search-input')) return
  e.preventDefault()
  e.stopPropagation()
  deleteSelectedObjects()
}

function onRowClick(e: MouseEvent, item: GroupItem) {
  if (editingId.value === item.id) return
  treeScrollRef.value?.focus()

  if (e.shiftKey) {
    const anchor = rangeAnchorId.value ?? item.id
    const range = rangeIds(anchor, item.id, flatVisibleIds.value)
    selectionStore.selectListRange(range)
    return
  }

  if (e.ctrlKey || e.metaKey) {
    selectionStore.listMultiToggle(item.id)
    rangeAnchorId.value = item.id
    return
  }

  rangeAnchorId.value = item.id
  selectionStore.requestFocus(item.id)
}

function onWindowClick() {
  closeCtxMenu()
}

onMounted(() => {
  window.addEventListener('click', onWindowClick)
})

onUnmounted(() => {
  window.removeEventListener('click', onWindowClick)
})

function toggleGroup(label: string) {
  if (expandedGroups.value.has(label)) {
    expandedGroups.value.delete(label)
  } else {
    expandedGroups.value.add(label)
  }
}

function toggleHidden(id: string) {
  emit('toggle-hidden', id)
}

function beginRename(item: GroupItem) {
  const obj = sceneStore.activeObjects().find((o) => o.id === item.id)
  if (!obj) return
  editingId.value = item.id
  renameDraft.value = obj.name?.trim() || baseDisplayName(obj)
  nextTick(() => {
    renameInputRef.value?.focus()
    renameInputRef.value?.select()
  })
}

function cancelRename() {
  editingId.value = null
}

function commitRename() {
  const id = editingId.value
  if (!id) return
  const obj = sceneStore.activeObjects().find((o) => o.id === id)
  if (!obj) {
    editingId.value = null
    return
  }
  const next = renameDraft.value.trim()
  const prev = obj.name?.trim() ?? ''
  if (next !== prev) {
    props.onHistoryPush?.()
    sceneStore.updateObject(id, { name: next })
  }
  editingId.value = null
}

watch(renameRequestId, (id) => {
  if (!id) return
  const obj = sceneStore.activeObjects().find((o) => o.id === id)
  if (!obj) {
    selectionStore.renameRequestId = null
    return
  }
  const def = registry.get(obj.type)
  const category = def?.category ?? 'building'
  const meta = CATEGORY_META[category] ?? { label: categoryLabelZh(category), iconComponent: Package }
  expandedGroups.value.add(meta.label)
  collapsed.value = false

  editingId.value = id
  renameDraft.value = obj.name?.trim() || baseDisplayName(obj)
  nextTick(() => {
    renameInputRef.value?.focus()
    renameInputRef.value?.select()
    selectionStore.renameRequestId = null
  })
})
</script>

<style scoped>
.obj-list-panel {
  position: relative;
  width: 300px;
  max-height: 35vh;
  flex-shrink: 0;
  pointer-events: auto;
  background: rgba(14, 24, 40, 0.95);
  border: 1px solid rgba(100, 160, 240, 0.25);
  border-radius: 0;
  border-right: none;
  border-top: none;
  border-bottom: 1px solid rgba(100, 160, 240, 0.25);
  backdrop-filter: blur(8px);
  display: flex;
  flex-direction: column;
  z-index: 200;
  overflow: hidden;
  transition: max-height 0.2s;
}

.obj-list-panel.collapsed {
  max-height: 40px;
}

.panel-header {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 10px;
  cursor: pointer;
  border-bottom: 1px solid rgba(100, 160, 240, 0.15);
  user-select: none;
  flex-shrink: 0;
  min-height: 40px;
  box-sizing: border-box;
}

.header-icon {
  width: 18px;
  height: 18px;
  flex-shrink: 0;
  color: #8ab4e8;
}

.header-title {
  flex: 1;
  font-size: 12px;
  font-weight: 600;
  color: #90bce0;
  white-space: nowrap;
  overflow: hidden;
}


.header-count {
  font-size: 11px;
  color: #607080;
}

.collapse-btn {
  font-size: 10px;
  color: #607080;
  margin-left: auto;
}

.panel-body {
  display: flex;
  flex-direction: column;
  min-height: 0;
  flex: 1;
}

.search-row {
  padding: 6px 8px;
  flex-shrink: 0;
}

.search-input {
  width: 100%;
  background: rgba(255, 255, 255, 0.07);
  border: 1px solid rgba(100, 160, 240, 0.2);
  border-radius: 4px;
  color: #c0d8f0;
  font-size: 11px;
  padding: 4px 8px;
  outline: none;
}

.search-input::placeholder {
  color: #406080;
}

.search-input:focus {
  border-color: rgba(100, 160, 240, 0.5);
}

.tree-scroll {
  flex: 1;
  overflow-y: auto;
  padding-bottom: 6px;
  outline: none;
}

.tree-scroll:focus-visible {
  box-shadow: inset 0 0 0 1px rgba(100, 160, 240, 0.35);
}

.tree-scroll::-webkit-scrollbar {
  width: 4px;
}

.tree-scroll::-webkit-scrollbar-track {
  background: transparent;
}

.tree-scroll::-webkit-scrollbar-thumb {
  background: rgba(100, 160, 240, 0.3);
  border-radius: 2px;
}

.group-header {
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 5px 8px 4px;
  cursor: pointer;
  user-select: none;
  font-size: 11px;
  color: #7090b0;
  transition: background 0.1s;
}

.group-header:hover {
  background: rgba(100, 160, 240, 0.08);
}

.group-arrow {
  font-size: 8px;
  width: 10px;
}

.group-icon-svg {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  color: #8899aa;
}

.group-name {
  flex: 1;
  font-weight: 600;
  letter-spacing: 0.02em;
}

.group-count {
  background: rgba(100, 160, 240, 0.15);
  color: #8ab0d0;
  border-radius: 10px;
  padding: 0 5px;
  font-size: 10px;
  font-weight: 600;
}

.obj-row {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 8px 4px 24px;
  cursor: pointer;
  font-size: 11px;
  color: #a0c0e0;
  border-left: 2px solid transparent;
  transition: background 0.1s, border-color 0.1s;
}

.obj-row:hover {
  background: rgba(100, 160, 240, 0.1);
}

.obj-row.selected {
  background: rgba(64, 130, 220, 0.2);
  border-left-color: #4088dd;
  color: #d0e8ff;
}

.obj-row.locked {
  opacity: 0.6;
}

.obj-row.hidden {
  opacity: 0.5;
}

.obj-row.hidden .obj-name {
  font-style: italic;
}

.obj-icon {
  display: inline-flex;
  align-items: center;
  font-size: 12px;
  flex-shrink: 0;
}
.obj-icon-svg {
  width: 16px;
  height: 16px;
}

.obj-name {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.obj-name-input {
  flex: 1;
  min-width: 0;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(100, 160, 240, 0.45);
  border-radius: 3px;
  color: #e0f0ff;
  font-size: 11px;
  padding: 2px 4px;
  outline: none;
}
.obj-name-input:focus {
  border-color: rgba(120, 180, 255, 0.7);
}

.obj-locked {
  width: 14px;
  height: 14px;
  flex-shrink: 0;
  color: #8899aa;
}

.obj-vis-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  margin: 0 -2px 0 2px;
  border: none;
  background: transparent;
  color: #8899aa;
  cursor: pointer;
  border-radius: 4px;
  flex-shrink: 0;
}
.obj-vis-btn:hover {
  background: rgba(255, 255, 255, 0.08);
  color: #aabbcc;
}

.empty-tip {
  padding: 16px;
  text-align: center;
  color: #405060;
  font-size: 12px;
}

.obj-list-ctx {
  position: fixed;
  z-index: 10000;
  min-width: 140px;
  padding: 4px 0;
  background: rgba(18, 28, 44, 0.98);
  border: 1px solid rgba(100, 160, 240, 0.35);
  border-radius: 6px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.45);
  font-size: 12px;
  color: #c0d8f0;
}

.obj-list-ctx-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  cursor: pointer;
  user-select: none;
}

.obj-list-ctx-label {
  flex: 1;
  min-width: 0;
}

.obj-list-ctx-kbd {
  margin-left: 12px;
  flex-shrink: 0;
  padding: 2px 6px;
  font-family: ui-monospace, 'Cascadia Mono', 'Segoe UI', monospace;
  font-size: 10px;
  font-weight: 600;
  line-height: 1.2;
  color: rgba(160, 190, 220, 0.85);
  background: rgba(0, 0, 0, 0.35);
  border: 1px solid rgba(100, 160, 240, 0.28);
  border-radius: 4px;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.06);
}

.obj-list-ctx-item:hover {
  background: rgba(100, 160, 240, 0.12);
}

.obj-list-ctx-item.disabled {
  opacity: 0.45;
  cursor: default;
}

.obj-list-ctx-item.disabled:hover {
  background: transparent;
}

.obj-list-ctx-sep {
  height: 1px;
  margin: 4px 0;
  background: rgba(100, 160, 240, 0.14);
}

.obj-list-ctx-item.danger {
  color: #ff8899;
}

.obj-list-ctx-item.danger:hover {
  background: rgba(180, 40, 60, 0.2);
}

.obj-list-ctx-icon {
  flex-shrink: 0;
  opacity: 0.9;
}
</style>
